/**
 * @file analytics.service.ts
 * @description Typed product-analytics facade over the consent-gated GTM
 * dataLayer. Every product event in the app flows through `track()`, which
 * stamps environment globals and the identified user (Cognito sub — never
 * PII) onto each push. Events fired after statistics-consent but before the
 * GTM script finishes loading are buffered (bounded) and flushed on load;
 * events fired without consent are dropped by design.
 *
 * Event taxonomy: docs/analytics/event-taxonomy.md — keep both in sync.
 */
import { effect, inject, Injectable } from '@angular/core';
import { GoogleTagManagerService } from '@kirigami/platform/browser';
import { environment } from '../../environments/environment';
import { CookieConsentService } from './cookie-consent.service';

/** Coarse plan tier attached to identity — never a raw subscription id. */
export type PlanType = 'none' | 'free' | 'pilot' | 'paid';

export type AnalyticsEvent =
  // The GTM config tag has send_page_view disabled, so the app is the single
  // owner of every page view (including the first one) — most frequent event.
  | { event: 'page_view'; page_path: string }
  | { event: 'login'; method: 'email' | 'google' }
  | { event: 'sign_up'; method: 'email' }
  | { event: 'sign_up_verified' }
  | { event: 'free_plan_activated'; grant_skipped: boolean }
  | { event: 'document_upload'; source: 'landing' | 'dashboard'; file_type: string }
  | { event: 'analysis_start'; mode: 'initial' | 'full' | 'sections'; report_id: string }
  | { event: 'analysis_complete'; report_id: string }
  | { event: 'analysis_fail'; report_id: string; fail_reason: 'failed' | 'timeout' }
  | { event: 'report_view'; report_id: string }
  | { event: 'upgrade_gate_show'; gate_reason: string }
  | { event: 'upgrade_gate_cta_click'; gate_reason: string }
  | { event: 'kb_create' }
  | { event: 'kb_doc_add' }
  | { event: 'kb_search' };

/** Upper bound on the consent-to-load buffer; older events are dropped first. */
const MAX_BUFFERED_EVENTS = 50;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly gtm = inject(GoogleTagManagerService);
  private readonly consentSvc = inject(CookieConsentService);

  /** Events fired after consent but before the GTM script finished loading. */
  private buffer: Record<string, unknown>[] = [];

  private userId: string | null = null;
  private planType: PlanType | null = null;
  private userRole: string | null = null;

  constructor() {
    effect(() => {
      if (this.gtm.isLoaded() && this.buffer.length > 0) {
        const pending = this.buffer;
        this.buffer = [];
        for (const item of pending) this.pushRaw(item);
      }
    });
  }

  /**
   * Record a product event. Pushes immediately when GTM is loaded; buffers
   * when statistics consent exists but the script is still loading; drops
   * silently otherwise (pre-consent events must never be transmitted).
   */
  track(e: AnalyticsEvent): void {
    this.enqueueOrPush({ ...e });
  }

  /** Attach the user identity (Cognito sub) to this and all later pushes. */
  identify(userId: string, props: { planType: PlanType; role?: string }): void {
    this.userId = userId;
    this.planType = props.planType;
    this.userRole = props.role ?? null;
    this.enqueueOrPush({ event: 'identify' });
  }

  /** Drop the identity (logout / session expiry). */
  clearIdentity(): void {
    this.userId = null;
    this.planType = null;
    this.userRole = null;
  }

  /**
   * Pushes immediately when GTM is loaded; buffers (bounded) when statistics
   * consent exists but the script is still loading; drops silently otherwise.
   */
  private enqueueOrPush(item: Record<string, unknown>): void {
    if (this.gtm.isLoaded()) {
      this.pushRaw(item);
      return;
    }
    if (this.consentSvc.consent()?.choices.statistics) {
      if (this.buffer.length >= MAX_BUFFERED_EVENTS) this.buffer.shift();
      this.buffer.push(item);
    }
  }

  /** Stamp globals + identity and forward to the dataLayer. */
  private pushRaw(item: Record<string, unknown>): void {
    this.gtm.pushTag({
      ...item,
      app_env: environment.stage,
      app_source: 'kirigami-app',
      ...(this.userId
        ? { user_id: this.userId, plan_type: this.planType, user_role: this.userRole }
        : {}),
    });
  }
}
